import React, { useEffect } from "react";
import {
  Box, Container, Typography, Grid, Card, Stack,
  Chip, IconButton, alpha, useTheme, Skeleton, Divider
} from "@mui/material";
import {
  CardGiftcard, LocalOffer, AccessTime,
  ArrowBack, CardMembership, Stars
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMyRewards } from "../redux/slices/wheel/thunk";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) => path?.startsWith("http") ? path : `${BASE_URL}${path}`;

export default function RewardsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const primaryMain = theme.palette.primary.main;

  const { myRewards, loading } = useSelector((state) => state.wheel);

  useEffect(() => {
    dispatch(getMyRewards());
  }, [dispatch]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 6 }}>
      {/* Dynamic Header */}
      <Box sx={{
        bgcolor: "background.paper",
        pt: { xs: 3, md: 5 },
        pb: { xs: 3, md: 5 },
        borderBottom: `1px solid ${theme.palette.divider}`,
        mb: 4
      }}>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: alpha(primaryMain, 0.08) }}>
              <ArrowBack sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={1000} sx={{ fontSize: { xs: '1.5rem', md: '2.1rem' } }}>
                {isRtl ? "محفظة المكافآت" : "Rewards Wallet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {isRtl ? "استعرض هداياك وجوائزك المتاحة" : "View your available gifts and prizes"}
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md">
        <Grid container spacing={2.5}>
          {loading ? (
            [1, 2, 3].map((i) => (
              <Grid item xs={12} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 4 }} />
              </Grid>
            ))
          ) : myRewards?.length > 0 ? (
            myRewards.map((reward) => (
              <Grid item xs={12} key={reward._id}>
                <RewardDisplayCard
                  reward={reward}
                  isRtl={isRtl}
                  formatDate={formatDate}
                  theme={theme}
                />
              </Grid>
            ))
          ) : (
            <Box sx={{ width: "100%", textAlign: "center", py: 12 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <CardMembership sx={{ fontSize: 100, color: alpha(theme.palette.text.disabled, 0.2) }} />
                <Stars sx={{ position: 'absolute', top: 0, right: 0, color: theme.palette.warning.light }} />
              </Box>
              <Typography variant="h6" color="text.secondary" fontWeight={800} sx={{ mt: 2 }}>
                {isRtl ? "المحفظة فارغة حالياً" : "Wallet is currently empty"}
              </Typography>
            </Box>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

function RewardDisplayCard({ reward, isRtl, formatDate, theme }) {
  const isFreeItem = reward.rewardType === "free_item";
  const accentColor = isFreeItem ? theme.palette.success.main : theme.palette.warning.main;

  return (
    <Card sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      borderRadius: 4,
      overflow: 'hidden',
      border: `1px solid ${alpha(accentColor, 0.15)}`,
      bgcolor: "background.paper",
      position: 'relative'
    }}>
      {/* Left Icon/Image Section */}
      <Box sx={{
        width: { xs: '100%', sm: 140 },
        height: { xs: 120, sm: 'auto' },
        bgcolor: alpha(accentColor, 0.04),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: isRtl ? 0 : { sm: `2px dashed ${alpha(accentColor, 0.2)}` },
        borderLeft: isRtl ? { sm: `2px dashed ${alpha(accentColor, 0.2)}` } : 0,
        position: 'relative'
      }}>
        {isFreeItem ? (
          <Box
            component="img"
            src={joinUrl(reward.freeItem?.image)}
            sx={{ width: 80, height: 80, objectFit: 'contain', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
          />
        ) : (
          <LocalOffer sx={{ fontSize: 50, color: accentColor }} />
        )}

        {/* Decorative Ticket Notches (Desktop Only) */}
        <Box sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'absolute', top: -10, [isRtl ? 'left' : 'right']: -10,
          width: 20, height: 20, borderRadius: '50%', bgcolor: 'background.default', border: `1px solid ${alpha(accentColor, 0.1)}`
        }} />
        <Box sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'absolute', bottom: -10, [isRtl ? 'left' : 'right']: -10,
          width: 20, height: 20, borderRadius: '50%', bgcolor: 'background.default', border: `1px solid ${alpha(accentColor, 0.1)}`
        }} />
      </Box>

      {/* Main Content */}
      <Stack sx={{ p: 2.5, flexGrow: 1 }} spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Chip
            label={isFreeItem ? (isRtl ? "هدية مجانية" : "Free Gift") : (isRtl ? "قسيمة خصم" : "Discount")}
            size="small"
            sx={{
              bgcolor: accentColor,
              color: '#fff',
              fontWeight: 900,
              fontSize: '0.7rem',
              height: 20
            }}
          />
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
            <AccessTime sx={{ fontSize: 14 }} />
            <Typography variant="caption" fontWeight={700}>
              {formatDate(reward.expiresAt)}
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="h6" fontWeight={1000} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
          {isRtl ? reward.name_ar : reward.name_en}
        </Typography>

        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {isFreeItem
            ? (isRtl ? `يمكنك استلام ${reward.freeItem?.title_ar} عند طلبك القادم` : `Receive ${reward.freeItem?.title_en} with your next order`)
            : (isRtl ? `سيتم تطبيق الخصم تلقائياً عند الدفع` : `Discount will be applied automatically at checkout`)
          }
        </Typography>
      </Stack>

      {/* Status Section */}
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(accentColor, 0.02),
        borderTop: { xs: `1px solid ${alpha(accentColor, 0.1)}`, sm: 0 }
      }}>
        <Typography
          variant="subtitle2"
          fontWeight={1000}
          sx={{
            color: accentColor,
            textTransform: 'uppercase',
            letterSpacing: 1,
            px: 2
          }}
        >
          {isRtl ? "نشط" : "Active"}
        </Typography>
      </Box>

      {/* Background Icon */}
      <CardGiftcard sx={{
        position: 'absolute',
        [isRtl ? 'left' : 'right']: 40,
        bottom: -15,
        fontSize: 80,
        opacity: 0.04,
        transform: 'rotate(-15deg)',
        pointerEvents: 'none'
      }} />
    </Card>
  );
}